import { Inbox, AlertTriangle, Plus } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { useTickets } from '@/queries/tickets'
import { EmptyState } from '@/components/ui/empty-state'
import { ListSkeleton } from '@/components/ui/skeleton'
import { TicketRow } from './TicketRow'
import { ViewingAsBanner } from '@/features/view-as/ViewingAsBanner'
import type { Ticket } from '@/types/ticket'

// Customer inbox. The admin queue (filters, search, sort) lives in AdminQueueView.
export function TicketListView({ hrefBase = '/tickets', asUser }: { hrefBase?: string; asUser?: string }) {
  const { data, isLoading, error, refetch } = useTickets({}, asUser)

  // A forged asUser on a non-admin session gets an explicit message, not a
  // blank inbox that reads as "you have no tickets".
  const status = (error as { status?: number; statusCode?: number } | null)?.status
    ?? (error as { statusCode?: number } | null)?.statusCode
  if (status === 403) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        You don&apos;t have access to view as another user.
      </div>
    )
  }

  return (
    <>
      {data?.viewingAs && <ViewingAsBanner name={data.viewingAs.name} companyName={data.viewingAs.companyName} />}
      <div className="flex gap-8">
        <div className="flex-1 min-w-0">
          <div className="mb-4 flex items-center justify-between">
            <h1 className="text-lg font-semibold text-etn-text">Tickets</h1>
            <Link to={'/tickets/new' as never} className="inline-flex">
              <Button size="sm">
                <Plus className="h-3.5 w-3.5" />
                New ticket
              </Button>
            </Link>
          </div>

          {isLoading && <ListSkeleton rows={5} />}

          {error && (
            <div className="flex flex-col items-center gap-3 py-16">
              <EmptyState
                icon={<AlertTriangle />}
                title="Couldn't load your tickets."
                body="Something went wrong. Please try again."
              />
              <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
            </div>
          )}

          {!isLoading && !error && (data?.data.length ?? 0) === 0 && (
            <EmptyState icon={<Inbox />} title="No tickets yet" body="When you open a support ticket it'll show up here." cta={{ label: 'Open a ticket', to: '/tickets/new' }} />
          )}

          {!isLoading && !error && (data?.data.length ?? 0) > 0 && (
            <div className="divide-y divide-etn-border-row rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper">
              {data!.data.map((t: Ticket) => (
                <TicketRow
                  key={t._id}
                  ticket={t}
                  relation={t.viewerRelation ?? 'owner'}
                  hrefBase={hrefBase}
                  asUser={asUser}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
